import { useState, useEffect } from 'react';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { comics, categories, faqData } from '@/mocks/comics';
import { Link, useNavigate } from 'react-router-dom';

const hotComics = comics.filter((c) => c.isHot);
const recommendedComics = comics.filter((c) => c.isRecommended);
const newComics = comics.filter((c) => c.isNew);
const completedComics = comics.filter((c) => c.status === '已完结');
const allComics = comics;

const heroBackgrounds = comics.slice(0, 5).map((c) => c.banner);

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '韩国漫画网',
  url: 'https://www.koreanmanhwa.cn/',
  description: '免费在线韩漫阅读平台，提供精彩漫画资源，涵盖恋爱、玄幻、悬疑、校园、科幻等多种类型。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.koreanmanhwa.cn/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩国漫画网',
  url: 'https://www.koreanmanhwa.cn/',
  description: '免费在线韩漫阅读平台',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'service@koreanmanhwa.cn',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

function HeroBanner() {
  const [currentBg, setCurrentBg] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % heroBackgrounds.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden" aria-label="首页横幅">
      {heroBackgrounds.map((bg, index) => (
        <div
          key={index}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: index === currentBg ? 1 : 0 }}
        >
          <img
            src={bg}
            alt={`韩国漫画推荐横幅 ${index + 1}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
      <div className="absolute inset-0 flex flex-col items-center justify-center w-full px-4">
        <div className="text-center max-w-3xl w-full">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            <a href="/" className="text-white hover:text-primary-300 transition-colors">
              韩国漫画网
            </a>
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
            免费在线韩漫阅读平台，汇集最新最全的<strong>韩国漫画资源</strong>，内容新颖更新快，随时随地畅享<strong>精彩漫画</strong>阅读体验
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
            <button
              onClick={() => navigate('/category/recommended')}
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              开始阅读
            </button>
            <button
              onClick={() => navigate('/category/recommended')}
              className="px-8 py-3 bg-white/10 text-white backdrop-blur-sm rounded-full font-medium text-sm hover:bg-white/20 transition-all cursor-pointer border border-white/20 whitespace-nowrap"
            >
              热门推荐
            </button>
          </div>
          <div className="flex items-center justify-center gap-6 mt-10">
            {[
              { num: '12+', label: '分类' },
              { num: '3000+', label: '漫画作品' },
              { num: '500万+', label: '月活跃读者' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.num}</div>
                <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroBackgrounds.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentBg(i)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              i === currentBg ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切换到横幅 ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

function RankingSidebar() {
  const ranked = [...comics].sort((a, b) => b.totalViews - a.totalViews).slice(0, 10);

  return (
    <div className="bg-background-50 border border-background-200/60 rounded-xl p-4 sticky top-20">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-6 h-6 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg">
          <i className="ri-fire-line text-sm"></i>
        </span>
        <h3 className="font-heading text-base font-bold text-foreground-900">人气排行</h3>
      </div>
      <div className="space-y-0.5">
        {ranked.map((comic, index) => (
          <Link
            key={comic.id}
            to={`/article/${comic.slug}`}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
          >
            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
              index < 3
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-200 text-foreground-600'
            }`}>
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground-800 truncate group-hover:text-primary-600 transition-colors">
                {comic.title}
              </p>
              <p className="text-xs text-foreground-400">{comic.category}</p>
            </div>
            <span className="text-xs text-accent-600 font-medium flex items-center gap-0.5 flex-shrink-0">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-[10px]"></i></span>
              {comic.rating}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <>
      <SEOHead
        title="韩国漫画网 - 免费在线韩漫阅读平台，精彩漫画资源"
        description="韩国漫画网提供免费在线韩漫阅读服务和精彩漫画资源，涵盖恋爱、玄幻、悬疑、校园、科幻等多种类型。内容新颖更新快，画质高清，满足您对高质量漫画故事的需求。随时随地畅享韩漫阅读体验。"
        keywords="韩国漫画网,免费在线韩漫,精彩漫画资源,韩漫阅读,韩国漫画,免费漫画,在线漫画"
        canonicalUrl="https://www.koreanmanhwa.cn/"
        schema={{ ...webSiteSchema, ...organizationSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <HeroBanner />

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-8 relative z-20" aria-label="漫画分类">
          <CategoryTabs activeSlug="recommended" />
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-5 mt-10">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Hot Comics */}
              <section className="mb-10 md:mb-14" aria-label="热门推荐">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg text-primary-600">
                      <i className="ri-fire-line"></i>
                    </span>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">
                      <a href="#hot-section">热门推荐</a>
                    </h2>
                  </div>
                  <Link to="/category/recommended" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                    查看全部 <span className="w-4 h-4 inline-flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {hotComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </section>

              {/* Featured Banner */}
              <section className="mb-10 md:mb-14" aria-label="精选推荐">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
                  {recommendedComics.slice(0, 2).map((comic) => (
                    <ComicCard key={comic.id} comic={comic} variant="featured" />
                  ))}
                </div>
              </section>

              {/* Latest Updates */}
              <section className="mb-10 md:mb-14" aria-label="最新更新">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-lg text-accent-600">
                      <i className="ri-timer-line"></i>
                    </span>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">
                      <a href="#latest-section">最新上架</a>
                    </h2>
                  </div>
                  <Link to="/category/romance" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                    查看全部 <span className="w-4 h-4 inline-flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {newComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </section>

              {/* Completed Works */}
              <section className="mb-10 md:mb-14" aria-label="已完结作品">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-secondary-100 rounded-lg text-secondary-600">
                      <i className="ri-check-double-line"></i>
                    </span>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">
                      <a href="#completed-section">已完结佳作</a>
                    </h2>
                  </div>
                  <Link to="/category/mystery" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                    查看全部 <span className="w-4 h-4 inline-flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {completedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-10 md:mb-14" aria-label="常见问题">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg text-primary-600">
                    <i className="ri-question-line"></i>
                  </span>
                  <h2 className="font-heading text-xl font-bold text-foreground-900">
                    <a href="#faq-section">常见问题</a>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
                  {faqData.map((faq, index) => (
                    <article key={index} className="p-5 bg-background-50 border border-background-200/60 rounded-xl" itemScope itemType="https://schema.org/Question">
                      <h3 className="font-semibold text-foreground-900 mb-2 text-sm" itemProp="name">{faq.question}</h3>
                      <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                        <p className="text-sm text-foreground-600 leading-relaxed" itemProp="text">{faq.answer}</p>
                      </div>
                    </article>
                  ))}
                </div>
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
                />
              </section>

              {/* All Comics */}
              <section className="mb-10 md:mb-14" aria-label="全部漫画">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-background-200 rounded-lg text-foreground-600">
                      <i className="ri-grid-line"></i>
                    </span>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">
                      <a href="#all-section">全部漫画</a>
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {allComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-[300px] flex-shrink-0 hidden lg:block">
              <RankingSidebar />
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}